import { useNavigate } from 'react-router-dom';

import {
  appColors,
  BackrsIconRow,
  Tier,
  CareerCategory,
  formatUTCDateTimeToLocaleString,
} from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/App';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import {
  CareerUserResponseSet,
  CareerPathWithScore,
  CareerQuestion,
} from '@backrs_ui/grow-ui/src/models/models';

import { Box, SxProps, Theme, Typography } from '@mui/material';

import { downloadPDF } from '../pdf/downloadPDF.util';
import { SECTION_GAP } from '../result.constant';
import { RESULT_PAGE_TOUR_IDS } from '../tour/ResultPage.tour';

const ACTION_STRINGS = appStrings.modules.results;

type ResultPageHeaderProps = Readonly<{
  tier: Tier;
  entrepreneurshipRecommended: boolean;
  userResponseSet: CareerUserResponseSet;
  careerPathSuggestions: CareerPathWithScore[];
  careerQuestions: CareerQuestion[];
  careerPathCategories: CareerCategory[];
  firstName?: string;
  lastName?: string;
  createdAt: string;
}>;

export const ResultPageHeader = ({
  tier,
  entrepreneurshipRecommended,
  userResponseSet,
  careerPathSuggestions,
  careerQuestions,
  careerPathCategories,
  firstName,
  lastName,
  createdAt,
}: ResultPageHeaderProps) => {
  const navigate = useNavigate();

  const fullName = firstName || lastName ? `${firstName} ${lastName}` : undefined;

  return (
    <Box sx={{ ...containerStyle, justifyContent: fullName ? 'space-between' : 'flex-end' }}>
      {fullName && (
        <Box sx={nameAndDateContainer}>
          <Typography variant="h6">{fullName}</Typography>
          <Typography variant="body1" sx={createdAtTextStyle}>
            {formatUTCDateTimeToLocaleString(createdAt)}
          </Typography>
        </Box>
      )}
      <Box sx={actionContainerStyle} id={RESULT_PAGE_TOUR_IDS.step9}>
        <BackrsIconRow
          icon="IconBookmark"
          text={ACTION_STRINGS.PDF.saveAsPDF}
          onClick={() =>
            downloadPDF({
              tier,
              entrepreneurshipRecommended,
              userResponseSet,
              careerPathSuggestions,
              careerQuestions,
              careerPathCategories,
              firstName,
              lastName,
              createdAt,
            })
          }
        />
        <BackrsIconRow
          icon="IconSend"
          text={ACTION_STRINGS.startOver}
          onClick={() => navigate(APP_ROUTE.INTRO)}
        />
      </Box>
    </Box>
  );
};

const containerStyle: SxProps<Theme> = {
  display: 'flex',
  flexDirection: {
    xs: 'column-reverse',
    sm: 'column-reverse',
    md: 'row',
  },
  mb: `${SECTION_GAP * 4}px`,
};

const actionContainerStyle: SxProps<Theme> = {
  borderRadius: '0px 0px 4px 4px',
  pl: 2,
  pr: 6.75,
  pt: 2.5,
  pb: 3,
  gap: 2.5,
  flexDirection: 'column',
  display: 'flex',
  backgroundColor: appColors.color.blue4,
  width: 'fit-content',
  alignSelf: 'flex-start',
};

const nameAndDateContainer: SxProps<Theme> = {
  backgroundColor: appColors.neutral.white,
  border: `2px solid ${appColors.neutral.black}`,
  borderRadius: '.5rem',
  pl: {
    xs: 2,
    sm: 4,
  },
  pb: 1.75,
  pt: 1.75,
  pr: 10,
  mt: `${28}px`,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
};

const createdAtTextStyle: SxProps<Theme> = {
  fontSize: '13px',
  '@media (max-width: 656px)': {
    fontSize: '10px',
  },
  color: appColors.color.blue1,
};
